// Shared pieces for the auth pages (/login, /signup, /forgot,
// /reset/:token): the glowing shell + card, the rotating headline, the
// Google button block, the inline form error, and error-message parsing.
// Each page mounts its own AuthShell, so the card's reveal animation
// replays on every route change.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { LoadingLinkButton } from "./Spinner";

// Carry next/invite through links between the auth pages so a deep link
// or invite flow survives switching from sign-in to sign-up to forgot.
export function authQuery(sp: URLSearchParams): string {
  const q = new URLSearchParams();
  const next = sp.get("next");
  const invite = sp.get("invite");
  if (next) q.set("next", next);
  if (invite) q.set("invite", invite);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function googleStartHref(sp: URLSearchParams): string {
  const next = sp.get("next") ?? "/";
  const invite = sp.get("invite") ?? "";
  return `/api/auth/google/start?next=${encodeURIComponent(next)}${
    invite ? `&invite=${encodeURIComponent(invite)}` : ""
  }`;
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative grid h-full place-items-center bg-bg p-6 overflow-hidden">
      <div className="auth-glow" aria-hidden="true" />
      <div className="card auth-card max-w-md w-full p-8 space-y-6 relative">
        {children}
      </div>
    </div>
  );
}

export function AuthLogo() {
  return <img src="/logo.png?v=5" alt="API Stash" className="mx-auto h-24 w-24" />;
}

// Google sign-in button + the "or" divider beneath it. Forgot-password
// deliberately doesn't render this — there's nothing to OAuth into.
export function GoogleBlock({ sp }: { sp: URLSearchParams }) {
  return (
    <>
      <LoadingLinkButton
        href={googleStartHref(sp)}
        className="btn-neutral w-full justify-center"
        pendingLabel="Connecting to Google…"
        startIcon={<GoogleIcon />}
      >
        Continue with Google
      </LoadingLinkButton>
      <div className="flex items-center gap-3 text-xs muted-strong">
        <div className="flex-1 h-px bg-border" />
        or
        <div className="flex-1 h-px bg-border" />
      </div>
    </>
  );
}

// Inline form error — danger-tinted card with an icon, slides in under
// the fields so the error sits next to what caused it.
export function FormError({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return (
    <div className="form-error" role="alert">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 mt-0.5">
        <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
      </svg>
      <span>{msg}</span>
    </div>
  );
}

export function parseErr(e: unknown, fallback: string): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("email_taken")) return "An account with that email already exists.";
  if (m.includes("weak_password")) return "Password must be at least 8 characters.";
  if (m.includes("bad_credentials")) return "Wrong email or password.";
  if (m.includes("bad_email")) return "That doesn't look like a valid email.";
  return fallback;
}

// Cycles through phrases with a slide-up rotation. The container is
// sized to the ACTIVE word (measured from invisible clones) and animates
// its width, so short phrases don't float inside the widest one's box.
export function RotatingWords({ words, interval = 2600 }: { words: string[]; interval?: number }) {
  const [i, setI] = useState(0);
  const measureRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setI((v) => (v + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);

  // Re-measure once webfonts land (metrics shift) and on window resize.
  useEffect(() => {
    const measure = () => {
      const el = measureRefs.current[i];
      if (el) setWidth(el.offsetWidth);
    };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [i]);

  const prev = (i - 1 + words.length) % words.length;
  return (
    <span className="rotator" aria-live="polite" style={width != null ? { width } : undefined}>
      {/* Zero-width in-flow strut: gives the container its line height
          and baseline so the absolutely-positioned words sit exactly on
          the sentence's baseline. */}
      <span className="rotator-strut" aria-hidden="true">&#8203;</span>
      <span className="rotator-measure" aria-hidden="true">
        {words.map((w, j) => (
          <span key={w} ref={(el) => { measureRefs.current[j] = el; }}>{w}</span>
        ))}
      </span>
      {words.map((w, j) => (
        <span key={w} className={"rotator-item " + (j === i ? "is-on" : j === prev ? "is-out" : "")} aria-hidden={j !== i}>
          {w}
        </span>
      ))}
    </span>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.9 28.9 4.9 24 4.9 16.1 4.9 9.3 9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8.2l-6.6 5.1C9.1 39.8 16 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.1 5.2C40.7 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
